// src/modules/shell-cliente/feed.js — V2-N Feed do cliente.
// Port do buildClienteFeed + handleLike + toggleComments + enviarComentario + compartilharPost (monolito).

import { sb } from '../../api/supabaseClient.js';
import { guardTrial, applyTrialLocks } from '../../lib/trialGate.js';
import { tenant } from '../../context/tenantContext.js';
import { showToast } from '../../lib/toast.js';
import './feed.css';

// ── Helpers internos ───────────────────────────────────────────────
function escHtml(t) {
  return String(t || '')
    .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
}
function escAttr(t) {
  return String(t || '')
    .replace(/'/g, '&apos;').replace(/"/g, '&quot;').slice(0, 80);
}
function timeAgo(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  const diff = Math.floor((Date.now() - d.getTime()) / 1000);
  if (diff < 60) return 'agora';
  if (diff < 3600) return Math.floor(diff / 60) + 'min';
  if (diff < 86400) return Math.floor(diff / 3600) + 'h';
  return Math.floor(diff / 86400) + 'd';
}
function nomeEmpresa() {
  return tenant.config?.nome || 'Angular ERP';
}

let _empresaNomeCache = null;
async function empresaNomeReal() {
  if (tenant.config?.nome) return tenant.config.nome;
  if (_empresaNomeCache) return _empresaNomeCache;
  try {
    const { data } = await sb.from('empresas').select('nome').eq('id', tenant.empresaId).maybeSingle();
    _empresaNomeCache = data?.nome || 'a construtora';
  } catch (e) { _empresaNomeCache = 'a construtora'; }
  return _empresaNomeCache;
}
function initialsOf(name) {
  return (name || nomeEmpresa()).split(' ').slice(0, 2).map((n) => n[0] || '').join('').toUpperCase();
}

// ── Renderiza o feed unificado (posts + notificações + tarefas concluídas) ──
export function buildFeedHTML(posts, notifs, tarefasConc) {
  const timeline = [
    ...(posts || []).map((p) => ({ ...p, _src: 'post' })),
    ...(notifs || []).map((n) => ({ ...n, _src: 'notif', notif_id: n.id })),
    ...(tarefasConc || []).map((t) => ({ ...t, _src: 'tarefa', created_at: t.data_conclusao || t.created_at })),
  ].filter((p) => p.created_at)
   .sort((a, b) => new Date(b.created_at) - new Date(a.created_at))
   .slice(0, 40);

  if (!timeline.length) {
    return '<div class="feed-empty">Nenhuma atualização ainda.<br>A equipe vai postar em breve.</div>';
  }

  const tipoIcon = { progresso: '📊', foto: '📸', mensagem: '💬', marco: '🏆', atualizacao: '🔔', aviso: '⚠️' };
  const usuarioId = tenant.user?.id;

  return timeline.map((p) => {
    // ─── Tarefa concluída
    if (p._src === 'tarefa') {
      const titulo = p.titulo || 'Tarefa';
      const obra = p.obras?.nome || '';
      const etapa = p.etapas_obra?.nome_etapa || '';
      return `<div class="feed-post" style="border-left:3px solid var(--sc-ok)">
        <div class="feed-post-header">
          <div class="feed-post-avatar" style="background:color-mix(in oklab, var(--sc-ok) 18%, transparent);color:var(--sc-ok);font-size:16px">✓</div>
          <div style="flex:1">
            <div class="feed-post-author" style="color:var(--sc-ok)">Tarefa concluída</div>
            <div class="feed-post-time">${timeAgo(p.created_at)} · ${escHtml(obra)}</div>
          </div>
        </div>
        <div class="feed-post-content"><b>${escHtml(titulo)}</b>${etapa ? ' · ' + escHtml(etapa) : ''}</div>
      </div>`;
    }

    // ─── Notificação de etapa concluída (gold card)
    if (p._src === 'notif' && p.tipo === 'etapa_concluida') {
      // Marca lida em background (best-effort)
      if (!p.lida && p.notif_id) {
        sb.from('notificacoes').update({ lida: true }).eq('id', p.notif_id).then(() => {}, () => {});
      }
      const etapaMatch = p.mensagem?.match(/›\s(.+?):/);
      const etapaNome = etapaMatch ? etapaMatch[1].trim() : '';
      const isFinal = /entrega|conclus|vistoria/i.test(etapaNome);
      if (isFinal) {
        return `<div class="feed-gold-etapa feed-gold-obra">
          <div class="feed-gold-header">
            <div class="feed-gold-badge">🏆</div>
            <div><div class="feed-gold-author">${escHtml(nomeEmpresa())} — Obra Entregue</div>
            <div class="feed-gold-time">${timeAgo(p.created_at)}</div></div>
          </div>
          <div class="feed-gold-stars">🏆 ✨ 🎉 ✨ 🏆</div>
          <div class="feed-gold-etapa-name">Entrega Final</div>
          <div class="feed-gold-title">Obra Entregue com Sucesso!</div>
          <div class="feed-gold-divider"></div>
          <div class="feed-gold-obra-msg">É com enorme satisfação que comunicamos a conclusão total da sua obra. Foi uma jornada de dedicação e técnica. Agradecemos a confiança!</div>
        </div>`;
      }
      return `<div class="feed-gold-etapa">
        <div class="feed-gold-header">
          <div class="feed-gold-badge">🌟</div>
          <div><div class="feed-gold-author">${escHtml(nomeEmpresa())} — Etapa Concluída</div>
          <div class="feed-gold-time">${timeAgo(p.created_at)}</div></div>
        </div>
        <div class="feed-gold-stars">⭐ 🌟 ⭐</div>
        <div class="feed-gold-etapa-name">${escHtml(etapaNome || 'Etapa')}</div>
        <div class="feed-gold-title">Etapa Concluída com 100%!</div>
        <div class="feed-gold-divider"></div>
        <div class="feed-gold-sub">${escHtml(p.mensagem || '')}</div>
      </div>`;
    }

    // ─── Notificação normal
    if (p._src === 'notif') {
      if (!p.lida && p.notif_id) {
        sb.from('notificacoes').update({ lida: true }).eq('id', p.notif_id).then(() => {}, () => {});
      }
      const ntMap = {
        etapa_atualizada: { icon: '🏗', cor: 'var(--sc-info)' },
        nova_foto:        { icon: '📸', cor: 'var(--sc-info)' },
        rdo_pendente:     { icon: '📋', cor: 'var(--sc-warn)' },
        default:          { icon: '🔔', cor: 'var(--sc-info)' },
      };
      const nt = ntMap[p.tipo] || ntMap.default;
      const titulo = (p.titulo || 'Atualização').replace(/^[\p{Extended_Pictographic}️‍\s]+/u, '') || 'Atualização';
      return `<div class="feed-post" style="border-left:3px solid ${nt.cor}">
        <div class="feed-post-header">
          <div class="feed-post-avatar" style="background:${nt.cor}22;color:${nt.cor};font-size:16px">${nt.icon}</div>
          <div style="flex:1">
            <div class="feed-post-author" style="color:${nt.cor}">${escHtml(titulo)}</div>
            <div class="feed-post-time">${timeAgo(p.created_at)}${!p.lida ? ` · <span style="color:${nt.cor};font-weight:700">Novo</span>` : ''}</div>
          </div>
        </div>
        ${p.mensagem ? `<div class="feed-post-content u-subtitle">${escHtml(p.mensagem)}</div>` : ''}
        ${p.tipo === 'rdo_pendente' ? `<div data-go="arquivos" style="margin-top:8px;display:inline-flex;align-items:center;gap:6px;font-size:12.5px;font-weight:700;color:var(--sc-warn);cursor:pointer">Abrir em Arquivos &amp; RDO →</div>` : ''}
      </div>`;
    }

    // ─── Post normal com interações
    const fotos = p.post_fotos || [];
    const likes = p.post_likes || [];
    const coments = p.post_comentarios || [];
    const jaCurtiu = likes.some((l) => l.usuario_id === usuarioId);
    const autor = p.users_profile?.nome || nomeEmpresa();
    const av = p.users_profile?.avatar_url
      ? `<img src="${p.users_profile.avatar_url}" alt="">`
      : escHtml(initialsOf(autor));

    const fotosHTML = fotos.length ? `
      <div class="feed-post-fotos ${fotos.length === 1 ? 'cols-1' : fotos.length === 2 ? 'cols-2' : 'cols-3'}">
        ${fotos.slice(0, 3).map((f) => `<img class="feed-post-foto" src="${escHtml(f.url)}" data-foto="${escHtml(f.url)}">`).join('')}
      </div>
    ` : '';

    const cmtHTML = coments.slice(0, 3).map((cm) =>
      `<div class="feed-comment"><strong>${escHtml(cm.users_profile?.nome || '?')}</strong> ${escHtml(cm.texto)}</div>`
    ).join('');

    return `<div class="feed-post" data-post-id="${escAttr(p.id)}">
      <div class="feed-post-header">
        <div class="feed-post-avatar">${av}</div>
        <div style="flex:1">
          <div class="feed-post-author">${escHtml(autor)}</div>
          <div class="feed-post-time">${timeAgo(p.created_at)}</div>
        </div>
        <span style="font-size:18px">${tipoIcon[p.tipo] || '📌'}</span>
      </div>
      ${fotosHTML}
      <div class="feed-post-content">${escHtml(p.conteudo)}</div>
      <div class="feed-post-actions">
        <button class="feed-action-btn ${jaCurtiu ? 'liked' : ''}" data-act="like" data-pid="${escAttr(p.id)}">
          ${jaCurtiu ? '❤️' : '🤍'} <span>${likes.length}</span>
        </button>
        <button class="feed-action-btn" data-act="comments" data-pid="${escAttr(p.id)}">
          💬 <span>${coments.length}</span>
        </button>
        <button class="feed-action-btn" data-act="share" data-pid="${escAttr(p.id)}" data-txt="${escAttr(p.conteudo)}" data-obra="${escAttr(p.obras?.nome || '')}">
          ↗ Compartilhar
        </button>
      </div>
      <div class="feed-comments" id="cmt-${escAttr(p.id)}" style="display:${coments.length ? 'block' : 'none'}">
        ${cmtHTML || '<div style="font-size:11px;color:var(--text2);padding:4px 0">Seja o primeiro a comentar!</div>'}
      </div>
      <div class="feed-comment-input">
        <input type="text" placeholder="Comentar..." id="cmtinput-${escAttr(p.id)}" data-pid="${escAttr(p.id)}">
        <button data-act="send-comment" data-pid="${escAttr(p.id)}">Enviar</button>
      </div>
    </div>`;
  }).join('');
}

// ── Wire-up dos handlers (delegação de eventos) ──────────────────────
export function bindFeedActions(container) {
  if (!container) return;
  applyTrialLocks(container);
  container.addEventListener('click', async (e) => {
    const btn = e.target.closest('[data-act]');
    if (btn) {
      const act = btn.dataset.act;
      const pid = btn.dataset.pid;
      if (act === 'like') return toggleLike(pid, btn);
      if (act === 'comments') return toggleComments(pid);
      if (act === 'share') return compartilharPost(pid, btn.dataset.txt, btn.dataset.obra);
      if (act === 'send-comment') return enviarComentario(pid);
    }
    const foto = e.target.closest('[data-foto]');
    if (foto) return window.open(foto.dataset.foto, '_blank');
  });
  container.addEventListener('keydown', (e) => {
    if (e.key !== 'Enter') return;
    const inp = e.target.closest('input[data-pid]');
    if (inp) enviarComentario(inp.dataset.pid);
  });
}

// ── Actions ──────────────────────────────────────────────────────────
async function toggleLike(postId, btn) {
  if (guardTrial()) return;

  if (!postId || !tenant.user?.id) return;
  const liked = btn.classList.contains('liked');
  const span = btn.querySelector('span');
  const n = parseInt(span?.textContent || '0', 10);
  // Optimistic UI
  btn.classList.toggle('liked');
  if (span) span.textContent = String(liked ? Math.max(0, n - 1) : n + 1);
  btn.innerHTML = (liked ? '🤍' : '❤️') + ` <span>${liked ? Math.max(0, n - 1) : n + 1}</span>`;
  btn.classList.toggle('liked', !liked);

  // Persist
  if (liked) {
    const { error } = await sb.from('post_likes')
      .delete().eq('post_id', postId).eq('usuario_id', tenant.user.id);
    if (error) { console.error('[feed] unlike', error); revertLike(btn, n, true); }
  } else {
    const { error } = await sb.from('post_likes')
      .insert({ post_id: postId, usuario_id: tenant.user.id });
    if (error) { console.error('[feed] like', error); revertLike(btn, n, false); }
  }
}

function revertLike(btn, originalCount, wasLiked) {
  btn.classList.toggle('liked', wasLiked);
  btn.innerHTML = (wasLiked ? '❤️' : '🤍') + ` <span>${originalCount}</span>`;
  showToast('Erro ao salvar curtida.', 'error');
}

function toggleComments(postId) {
  const el = document.getElementById('cmt-' + postId);
  if (el) el.style.display = el.style.display === 'none' ? 'block' : 'none';
}

async function enviarComentario(postId) {
  if (guardTrial()) return;

  if (!postId || !tenant.user?.id) return;
  const inp = document.getElementById('cmtinput-' + postId);
  const txt = inp?.value?.trim();
  if (!txt) return;

  const { error } = await sb.from('post_comentarios').insert({
    post_id: postId,
    usuario_id: tenant.user.id,
    texto: txt,
  });
  if (error) {
    showToast('Erro ao enviar: ' + error.message, 'error');
    return;
  }
  inp.value = '';
  // Optimistic: insere no DOM
  const cmt = document.getElementById('cmt-' + postId);
  if (cmt) {
    const novoNome = tenant.profile?.nome || 'Você';
    const div = document.createElement('div');
    div.className = 'feed-comment';
    div.innerHTML = `<strong>${escHtml(novoNome)}</strong> ${escHtml(txt)}`;
    cmt.insertBefore(div, cmt.firstChild);
    cmt.style.display = 'block';
  }
  showToast('✓ Comentário enviado!');
}

function _wrapTextCanvas(ctx, text, x, y, maxW, lineH, maxLines) {
  const words = String(text || '').split(/\s+/).filter(Boolean);
  if (!words.length) return y;
  let line = '';
  let linhas = 0;
  for (let i = 0; i < words.length; i++) {
    const test = line ? line + ' ' + words[i] : words[i];
    if (ctx.measureText(test).width > maxW && line) {
      ctx.fillText(line, x, y);
      linhas += 1; y += lineH; line = words[i];
      if (linhas >= maxLines - 1) {
        let resto = words.slice(i).join(' ');
        while (resto && ctx.measureText(resto + '…').width > maxW) resto = resto.slice(0, -1);
        ctx.fillText(resto + '…', x, y);
        return y + lineH;
      }
    } else { line = test; }
  }
  ctx.fillText(line, x, y);
  return y + lineH;
}

async function _gerarCardObra(fotoUrl, obraNome, legenda, empresa) {
  const resp = await fetch(fotoUrl);
  const blob = await resp.blob();
  const url = URL.createObjectURL(blob);
  const img = await new Promise((res, rej) => { const i = new Image(); i.onload = () => res(i); i.onerror = rej; i.src = url; });
  const W = 1080, H = 1080;
  const cv = document.createElement('canvas');
  cv.width = W; cv.height = H;
  const ctx = cv.getContext('2d');
  ctx.fillStyle = '#0E0E14'; ctx.fillRect(0, 0, W, H);
  const r = Math.max(W / img.width, H / img.height);
  const dw = img.width * r, dh = img.height * r;
  ctx.drawImage(img, (W - dw) / 2, (H - dh) / 2, dw, dh);
  URL.revokeObjectURL(url);
  const g = ctx.createLinearGradient(0, H * 0.40, 0, H);
  g.addColorStop(0, 'rgba(8,11,25,0)');
  g.addColorStop(0.55, 'rgba(8,11,25,0.80)');
  g.addColorStop(1, 'rgba(8,11,25,0.97)');
  ctx.fillStyle = g; ctx.fillRect(0, H * 0.40, W, H * 0.60);
  const pad = 64;
  const F = "system-ui, -apple-system, 'Segoe UI', Roboto, sans-serif";
  ctx.textBaseline = 'alphabetic';
  ctx.fillStyle = '#34D399';
  ctx.font = '700 28px ' + F;
  ctx.fillText('ACOMPANHAMENTO DE OBRA', pad, H - 380);
  ctx.fillStyle = '#ffffff';
  ctx.font = '800 56px ' + F;
  let y = _wrapTextCanvas(ctx, obraNome || 'Minha obra', pad, H - 320, W - pad * 2, 62, 2);
  ctx.fillStyle = 'rgba(255,255,255,0.88)';
  ctx.font = '400 36px ' + F;
  _wrapTextCanvas(ctx, legenda || 'Mais uma etapa concluida!', pad, y + 6, W - pad * 2, 46, 2);
  ctx.fillStyle = '#7C5CFC';
  ctx.font = '700 38px ' + F;
  ctx.fillText('✨ ' + (empresa || 'Construtora'), pad, H - 60);
  return await new Promise((res) => cv.toBlob((b) => res(b), 'image/jpeg', 0.9));
}

async function compartilharPost(postId, conteudo, obraNome) {
  const empresa = await empresaNomeReal();
  const corpo = (conteudo && conteudo.trim()) ? conteudo.trim() : 'Mais uma etapa concluída!';
  const _linhas = [
    '🏗 *Minha obra está evoluindo!*',
    obraNome ? `📍 ${obraNome}` : null,
    '',
    corpo,
    '',
    `✨ Um projeto executado por *${empresa}*`,
    'Acompanhamento em tempo real, do alicerce à entrega. 🔑',
  ].filter((l) => l !== null);
  const texto = _linhas.join('\n');
  // 1) CARD: foto + legenda + crédito embutidos numa imagem só → WhatsApp não descarta.
  try {
    const cardEl = document.querySelector(`[data-post-id="${CSS.escape(String(postId))}"]`);
    const imgEl = cardEl?.querySelector('.feed-post-foto');
    if (imgEl?.src && navigator.canShare) {
      const blob = await _gerarCardObra(imgEl.src, obraNome, corpo, empresa);
      if (blob) {
        const file = new File([blob], 'obra.jpg', { type: 'image/jpeg' });
        const shareData = { title: empresa, text: texto, files: [file] };
        if (navigator.canShare(shareData)) { await navigator.share(shareData); return; }
      }
    }
  } catch (e) { console.warn('[share] card falhou, fallback texto:', e); }

  // 2) Fallback: só o texto.
  if (navigator.share) {
    try { await navigator.share({ title: empresa, text: texto }); } catch (e) { /* cancelou */ }
    return;
  }
  try {
    await navigator.clipboard.writeText(texto);
    showToast('✓ Texto copiado!');
  } catch {
    showToast('Não foi possível copiar.', 'error');
  }
}
